import { type ApiProblem, type AxiosApiProblem, isAxiosApiProblem } from "@vyne-shared/api/errors";
import { isAxiosError } from "axios";
import configuration from "utils/config";
import type { ModalFuncProps } from "antd";

/** API response bearing an exception that has been configured to `MapToStatusCodeAndExpose` in the server code. */
export interface ApiException extends Omit<ApiProblem, "title"> {
    exceptionType: string;
}

/**
 * Check if the given error indicates missing legal business information for the active Provider.
 * @param error The error message to inspect.
 * @returns True if the error conforms to the requirements, false otherwise.
 */
export function isMissingLegalInfoError(error: unknown): error is AxiosApiProblem {
    return isAxiosApiProblem(error) && error.response.status === 451;
}

/** Standard modal dialog to display when legal business information is missing. */
export const missingLegalInfoModal: ModalFuncProps = {
    title: "Legal Business Information Required",
    okText: "Take me there",
    content:
        "Due to SMS regulations, you must successfully verify your office as a legal business entity by supplying your information in the My Practice section.",
    closable: true,
    okButtonProps: {
        href: configuration.trellisAppUrl + "Practice/PracticeInfo",
        target: "_top",
    },
};

const rxExceptionType = /^[\w]+Exception$/;

/**
 * Given an error object, provide information about an API exception, if the error is due to an exception that has been
 * configured to `MapToStatusCodeAndExpose` in the server app code.
 *
 * For errors other than those configured to `MapToStatusCodeAndExpose`, provide an {@link ApiException} object with its
 * `detail` property set to {@link fallbackDetail}.
 */
export function getApiException(error: unknown, fallbackDetail: string): ApiException {
    if (isAxiosApiProblem(error) && rxExceptionType.test(error.response.data.title)) {
        const { title: exceptionType, status, detail } = error.response.data;
        return { exceptionType, status, detail };
    } else if (isAxiosError(error) && typeof error.response?.status === "number") {
        return { exceptionType: "UnknownException", status: error.response.status, detail: fallbackDetail };
    } else {
        return { exceptionType: "UnknownException", status: 418, detail: fallbackDetail };
    }
}
